import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getPulseTokens } from '../../services/pulse-api';

const PulseTokensSidebar = ({ currentTokenAddress, chainId }) => {
  const navigate = useNavigate();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchInProgressRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate fetches
    if (fetchInProgressRef.current) return;
    
    const fetchTokens = async () => {
      fetchInProgressRef.current = true;
      try {
        setLoading(true);
        const fetchedTokens = await getPulseTokens(chainId);
        
        // Deduplicate tokens by contract address
        const uniqueTokens = [];
        const seenAddresses = new Set();
        
        fetchedTokens.forEach(token => {
          const ca = token.ca || token.tokenAddress;
          if (ca && !seenAddresses.has(ca.toLowerCase())) {
            seenAddresses.add(ca.toLowerCase());
            uniqueTokens.push(token);
          }
        });
        
        setTokens(uniqueTokens);
      } catch (error) {
        console.error('Error fetching pulse tokens:', error);
      } finally {
        setLoading(false);
        fetchInProgressRef.current = false;
      }
    };

    fetchTokens();
  }, [chainId]);

  const getChainPath = (chainId) => {
    const chainPathMap = {
      ethereum: "0x1",
      binance: "0x38",
      bsc: "0x38",
      polygon: "0x89",
      solana: "solana",
      arbitrum: "0xa4b1",
      base: "0x2105",
      avalanche: "0xa86a",
      optimism: "0xa",
      linea: "0xe708",
      fantom: "0xfa",
      pulse: "0x171",
      ronin: "0x7e4",
    };
    return chainPathMap[chainId] || chainId;
  };

  const handleTokenClick = (token) => {
    const ca = token.ca || token.tokenAddress;
    if (!ca) return;
    
    const chainPath = getChainPath(chainId);
    navigate(`/${chainPath}/${ca}`);
  };

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="p-4">
        <h3 className="text-sm font-pp-mori font-semibold text-white mb-4">
          Active Signals
        </h3>
        <div className="space-y-3">
          {tokens.map((token, index) => {
            const ca = token.ca || token.tokenAddress;
            // Use a unique key combining address and index to ensure uniqueness
            const uniqueKey = ca ? `${ca}-${index}` : `token-${index}`;
            const isActive = ca && currentTokenAddress && 
              ca.toLowerCase() === currentTokenAddress.toLowerCase();
            
            const tokenSymbol = token.tokenSymbol || token.ticker || null;
            const tokenName = token.tokenName || null;
            const tokenImage = token.tokenImage || null;
            const displayAddress = ca ? `${ca.substring(0, 6)}...${ca.substring(ca.length - 4)}` : "Unknown";

            return (
              <motion.div
                key={uniqueKey}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onClick={() => handleTokenClick(token)}
                className={`group cursor-pointer rounded-xl p-3 transition-all duration-200 ${
                  isActive
                    ? 'bg-white/20 border-2 border-white/40'
                    : 'bg-white/10 border border-white/20 hover:bg-white/15 hover:border-white/30'
                }`}
              >
                <div className="flex items-center gap-3">
                  {/* Token Image/Avatar */}
                  <div className="h-10 w-10 rounded-lg flex-shrink-0 overflow-hidden relative">
                    {tokenImage ? (
                      <img
                        src={tokenImage}
                        alt={tokenSymbol || tokenName || "Token"}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const fallback = e.target.nextElementSibling;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`h-full w-full bg-gradient-to-br from-[#151a1a] to-[#2d3436] flex items-center justify-center ${tokenImage ? 'hidden absolute inset-0' : ''}`}
                    >
                      {tokenSymbol ? (
                        <span className="text-white font-semibold text-xs font-pp-mori">
                          {tokenSymbol.substring(0, 3).toUpperCase()}
                        </span>
                      ) : (
                        <span className="text-white font-semibold text-xs font-pp-mori">
                          {displayAddress.substring(0, 2).toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Token Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {tokenSymbol && (
                        <span className="text-sm font-pp-mori font-semibold text-white">
                          ${tokenSymbol}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-white/70 font-mono truncate font-inter">
                      {displayAddress}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PulseTokensSidebar;

